import { ModalEventBoundary } from './ui/modal-event-boundary'
import React, { useState, useEffect } from 'react'
import { useProfilePictures } from '../contexts/ProfilePictureContext'
import { apiClient } from '../common/apiClient'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { X } from 'lucide-react'

interface AdminOverrideModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectUser: (email: string) => void
}

interface Staff {
  Id: string
  Name: string
  Email: string
  Title?: string
  Team__c?: string
  Staff_Subdivision__c?: string
  Division_Location__c?: string
}

const AdminOverrideModal: React.FC<AdminOverrideModalProps> = ({
  isOpen,
  onClose,
  onSelectUser,
}) => {
  const { getProfilePicture, fetchProfilePictures } = useProfilePictures()
  const [staff, setStaff] = useState<Staff[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Fetch Duo Tax staff from Salesforce
  useEffect(() => {
    const fetchStaff = async () => {
      if (!isOpen) return

      setIsLoading(true)
      try {
        const data = await apiClient.get<Staff[]>('/api/minutes/staff')
        setStaff(data)

        // Prefetch profile pictures for first 20 staff
        const emails = data.slice(0, 20).map((s) => s.Email)
        fetchProfilePictures(emails, '48x48')
      } catch (error) {
        console.error('Error fetching staff:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchStaff()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <ModalEventBoundary className="fixed inset-0 z-40" onClick={onClose}>
      {/* Backdrop - subtle overlay */}
      <div className="absolute inset-0 bg-black/20 transition-opacity animate-in fade-in-0" />

      {/* Bottom-left popover card with smart positioning */}
      <div
        className="fixed bottom-4 left-4 z-50 w-[420px] animate-in slide-in-from-bottom-2 fade-in-0 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rounded-xl border border-gray-200 bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">View As User</h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Select a user to view their perspective
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-md opacity-70 transition-opacity hover:opacity-100 hover:bg-gray-100 p-1.5 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-white"
              aria-label="Close"
            >
              <X className="h-4 w-4 text-gray-600" />
            </button>
          </div>

          {/* Command Component */}
          <Command className="rounded-none border-0 bg-white text-gray-900">
            <CommandInput
              placeholder="Search staff by name..."
              className="h-10 bg-white text-gray-900 placeholder:text-gray-500 border-gray-200"
              autoFocus
            />
            <CommandList className="max-h-[400px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-gray-400 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300">
              <CommandEmpty className="text-gray-600 py-6 px-4">
                {isLoading ? 'Loading staff...' : 'No staff found.'}
              </CommandEmpty>
              <CommandGroup>
                {staff.map((s) => {
                  const profilePicture = getProfilePicture(s.Email, '48x48')
                  return (
                    <CommandItem
                      key={s.Id}
                      value={s.Name}
                      onSelect={() => {
                        onSelectUser(s.Email)
                        onClose()
                      }}
                      className="cursor-pointer data-[selected=true]:bg-gray-100 text-gray-900 rounded-lg"
                    >
                      <Avatar className="h-8 w-8 mr-2">
                        <AvatarImage src={profilePicture} alt={s.Name} />
                        <AvatarFallback className="text-xs bg-orange-600 text-white">
                          {s.Name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">{s.Name}</span>
                        {s.Title && (
                          <span className="text-xs text-gray-600 truncate">
                            {s.Title}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      </div>
    </ModalEventBoundary>
  )
}

export default AdminOverrideModal
